import { gql, MutationHookOptions, useMutation } from "@apollo/client";
import {
    segmentTrackUnlockBusAttempt,
    segmentTrackUnlockBusConfirmationConfirmed
} from "analytics/analyticTrackEvents";
import { useMapViewRoutingMetadata } from "contexts/RoutingMetadataContext";
import useSelectedScopedBus from "contexts/SelectedScopedBusContext/useSelectedScopedBus";
import {
    UnlockBusMutation,
    UnlockBusMutationVariables
} from "generated/graphql";
import { loader } from "graphql.macro";
import { useEffect, useRef, useState } from "react";
import { NiraBusId } from "types/busType";
import {
    ComponentConfig,
    isCurtailmentConfig,
    isPricedConstraintConfig
} from "types/componentConfigPerRegion";
import {
    UNLOCK_STATE_ALREADY_UNLOCKED,
    UNLOCK_STATE_LOADING,
    UNLOCK_STATE_NEEDS_CONFIRMATION,
    UNLOCK_STATE_UNAVAILABLE
} from "./busUnlockState";
import ConstraintsBasedBusDetailPane from "./ConstraintsBasedBusDetailPane";
import { default as CurtailmentBusDetailPane } from "./CurtailmentBusDetailPane";
import UnauthorizedDetailPane from "./UnauthorizedBusDetailPane";
import UnlockPromptBusDetailPane from "./UnlockPromptBusDetailPane";
import useBusUnlockState from "./useBusUnlockState";

const UNLOCK_BUS = loader("src/graphql/unlockBus.graphql");
const UNLOCKED_FIELDS_FRAGMENT = gql`
    fragment UnlockedBus on buses {
        unlocked {
            unlocked
        }
    }
`;
const UNLOCKED_DATA = {
    unlocked: [
        {
            unlocked: true
        }
    ]
};

const PermissionedBusDetailPane: React.FC = () => {
    const { componentConfig } = useMapViewRoutingMetadata();
    const scopedBus = useSelectedScopedBus();
    const { bus } = scopedBus;

    const unlockState = useBusUnlockState(bus);

    // The bus the user has explicitly agreed to spend, tracked per bus so that
    // confirming one bus can never unlock the next one they click.
    const [confirmedNiraBusId, setConfirmedNiraBusId] =
        useState<NiraBusId | null>(null);
    const isConfirmedForSelectedBus = confirmedNiraBusId === bus.niraBusId;

    const isAwaitingConfirmation =
        unlockState.kind === UNLOCK_STATE_NEEDS_CONFIRMATION &&
        !isConfirmedForSelectedBus;

    // Unlock as soon as the bus is selected, the way the app always has, unless
    // we're still working out whether we owe a confirmation, are waiting on one,
    // the bus is already unlocked (where the mutation is a no-op round trip), or
    // we couldn't establish whether unlocking would spend a bus at all.
    const shouldUnlockBus =
        unlockState.kind !== UNLOCK_STATE_LOADING &&
        unlockState.kind !== UNLOCK_STATE_ALREADY_UNLOCKED &&
        unlockState.kind !== UNLOCK_STATE_UNAVAILABLE &&
        !isAwaitingConfirmation;

    const lastNiraBusIdAttemptedToUnlock = useRef<NiraBusId | null>(null);

    const unlockBusOptions: MutationHookOptions<
        UnlockBusMutation,
        UnlockBusMutationVariables
    > = {
        // whenever the unlockBus mutation is called, manually update the unlocked field of the locally cached bus
        // without this, we would need to refetch the bus from the network every time the line tap pane is loaded to get the updated unlocked field
        update: (cache, { data }) => {
            if (data?.unlock_bus == null) {
                return;
            }

            const busCacheId = `buses:${data.unlock_bus.nira_bus_id}`;
            cache.writeFragment({
                id: busCacheId,
                fragment: UNLOCKED_FIELDS_FRAGMENT,
                data: UNLOCKED_DATA
            });
        }
    };
    const [unlockBusMutation, { data, loading, error }] = useMutation<
        UnlockBusMutation,
        UnlockBusMutationVariables
    >(UNLOCK_BUS, unlockBusOptions);

    useEffect(() => {
        if (!shouldUnlockBus) {
            return;
        }
        const niraBusId = bus.niraBusId;
        lastNiraBusIdAttemptedToUnlock.current = niraBusId;
        unlockBusMutation({
            variables: { niraBusId },
            onCompleted: (unlockBusData) => {
                segmentTrackUnlockBusAttempt(
                    niraBusId,
                    !busUnlockFailed(unlockBusData)
                );
            },
            // The request never completed, so nothing was spent -- but the
            // attempt still happened, and the funnel would otherwise lose it.
            // Handling it here also stops Apollo re-throwing into an unhandled
            // rejection: `useMutation` only swallows the error when an
            // `onError` is present.
            onError: () => {
                segmentTrackUnlockBusAttempt(niraBusId, false);
            }
        });
    }, [bus.niraBusId, shouldUnlockBus, unlockBusMutation]);

    // Ask first: show the bus's free-to-view details with the unlock question in
    // place of the study results, instead of quietly spending one of their buses.
    if (
        unlockState.kind === UNLOCK_STATE_NEEDS_CONFIRMATION &&
        !isConfirmedForSelectedBus
    ) {
        return (
            <UnlockPromptBusDetailPane
                busesRemaining={unlockState.busesRemaining}
                numBuses={unlockState.numBuses}
                isStatusVerified={unlockState.isStatusVerified}
                onUnlock={() => {
                    segmentTrackUnlockBusConfirmationConfirmed(
                        bus.niraBusId,
                        unlockState.busesRemaining
                    );
                    setConfirmedNiraBusId(bus.niraBusId);
                }}
            />
        );
    }

    // The status check failed and login-time data couldn't answer for it, so
    // nothing was attempted: say we couldn't unlock it rather than blaming a bus
    // count we were never able to read.
    if (unlockState.kind === UNLOCK_STATE_UNAVAILABLE) {
        return (
            <UnauthorizedDetailPane
                scopedBus={scopedBus}
                unlockRequestErrored
            />
        );
    }

    let unlockLoading = false;

    if (unlockState.kind === UNLOCK_STATE_ALREADY_UNLOCKED) {
        // Nothing to unlock: this team already owns the bus, so the pane can
        // render its data right away.
        unlockLoading = false;
    } else if (
        error !== undefined &&
        !loading &&
        lastNiraBusIdAttemptedToUnlock.current === bus.niraBusId
    ) {
        // The unlock request itself failed, so `data` never arrives and the
        // `data === undefined` branch below would spin forever. Nothing was
        // spent; say so instead. The two guards keep a previous bus' error from
        // flashing this pane at a bus whose own mutation hasn't run yet.
        return (
            <UnauthorizedDetailPane
                scopedBus={scopedBus}
                unlockRequestErrored
            />
        );
    } else if (
        unlockState.kind === UNLOCK_STATE_LOADING ||
        loading ||
        data === undefined ||
        data === null ||
        // If the bus_id the last time we unlocked a bus is different from the bus id we are trying
        // to render, then the mutation for the new bus hasn't been called yet.
        // In that case, we just render this as "loading" since we are sure that the
        // mutation will re-execute due to the useEffect above.
        lastNiraBusIdAttemptedToUnlock.current !== bus.niraBusId
    ) {
        // unlockBusMutation is loading, so don't render anything quite yet
        unlockLoading = true;
    } else if (busUnlockFailed(data)) {
        return <UnauthorizedDetailPane scopedBus={scopedBus} />;
    }

    return getBusDetailPane(componentConfig, unlockLoading);
};

const getBusDetailPane = (
    componentConfig: ComponentConfig,
    unlockBusLoading: boolean
): JSX.Element | null => {
    if (isPricedConstraintConfig(componentConfig)) {
        return (
            <ConstraintsBasedBusDetailPane
                unlockBusLoading={unlockBusLoading}
            />
        );
    } else if (isCurtailmentConfig(componentConfig)) {
        return <CurtailmentBusDetailPane unlockBusLoading={unlockBusLoading} />;
    } else {
        return null;
    }
};

// The unlock_bus mutation returns null when it could not unlock the bus -- the
// team is out of buses, has no subscription in the region, or the user's role
// isn't allowed to unlock.
const busUnlockFailed = (unlockBusResponse: UnlockBusMutation): boolean => {
    return unlockBusResponse.unlock_bus == null;
};

export default PermissionedBusDetailPane;
